/**
 * MobileSessionHistory — PRI-494 P3
 *
 * Full-screen overlay showing merged session history from:
 *   - in-memory resultPills (StreamContext)
 *   - streamUserVideos (VideoLibraryContext)
 *   - regularUserVideos (VideoLibraryContext)
 *
 * Features: date grouping, All/Live/Uploads filter, swipe-to-delete,
 * pinned LIVE row, thumbnail fallbacks, empty state, virtualization >50 rows.
 */
import { useState, useEffect, useRef, useCallback, type RefObject } from 'react'
import {
  L,
  MOBILE_ZONES,
  fmtTime,
  stripCompiledQueryEnvelope,
  type ResultPill,
} from '../../core/components/v2/pv2-shared'
import type { UserVideo } from '../../core/lib/api/user-videos'
import {
  trackMobileHistoryOpened,
  trackMobileSessionReopened,
  trackMobileHistoryFilterUsed,
} from '../../core/telemetry/events'

// ── Types ─────────────────────────────────────────────────────────────────────

type HistoryFilter = 'all' | 'live' | 'uploads'

interface HistoryRow {
  key: string
  kind: 'live' | 'upload'
  thumb: string | null
  prompt: string
  createdAt: Date
  durationSeconds: number | null
  answer: 'YES' | 'NO' | null
  confidence: number | null
  queryType?: string
  // reopen data
  pill?: ResultPill          // if backed by in-memory pill
  userVideoId?: string       // if backed by persisted UserVideo
}

export interface MobileSessionHistoryProps {
  open: boolean
  onClose: () => void
  // Stream state
  isStreaming: boolean
  streamPillIdRef: RefObject<string | null>
  resultPills: ResultPill[]
  // Library data
  streamUserVideos: UserVideo[]
  regularUserVideos: UserVideo[]
  // Handlers
  handleDeleteResult: (pill: ResultPill) => void
  deleteVideo: (id: string) => void
  handleSelectUserVideo: (videoId: string) => void
  attachRenderedVideoForced: (pillId: string) => void
  setVideoMenuOpen: (v: boolean) => void
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTime(date: Date): string {
  const now = new Date()
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  if (isToday) {
    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function groupLabel(date: Date): string {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today.getTime() - 86400000)
  const rowDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  if (rowDay.getTime() === today.getTime()) return 'Today'
  if (rowDay.getTime() === yesterday.getTime()) return 'Yesterday'
  const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' }
  if (date.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return date.toLocaleDateString([], opts)
}

// Build merged, deduped, sorted list of HistoryRows
function buildRows(
  resultPills: ResultPill[],
  streamUserVideos: UserVideo[],
  regularUserVideos: UserVideo[],
): HistoryRow[] {
  const rows: HistoryRow[] = []

  // Track which userVideoIds are already represented by in-memory pills
  const inMemoryUserVideoIds = new Set<string>()
  for (const p of resultPills) {
    if (p.userVideoId) inMemoryUserVideoIds.add(p.userVideoId)
  }

  // 1. In-memory pills (newest-first order is preserved from the resultPills array)
  for (const pill of resultPills) {
    const kind: 'live' | 'upload' = pill.id.startsWith('stream-') ? 'live' : 'upload'
    const prompt = stripCompiledQueryEnvelope(pill.prompt)
    const createdAt = pill.savedAt ? new Date(pill.savedAt) : new Date()
    rows.push({
      key: `pill-${pill.id}`,
      kind,
      thumb: pill.videoThumb ?? null,
      prompt,
      createdAt,
      durationSeconds: null,
      answer: pill.answer ?? null,
      confidence: pill.confidence ?? null,
      queryType: pill.query_type,
      pill,
    })
  }

  // 2. Persisted stream UserVideos (skip if represented by in-memory pill)
  for (const uv of streamUserVideos) {
    if (inMemoryUserVideoIds.has(uv.id)) continue
    const firstResult = Object.values(uv.results_by_prompt)[0]
    const firstPromptKey = Object.keys(uv.results_by_prompt)[0]
    const prompt = stripCompiledQueryEnvelope(firstPromptKey ?? uv.display_name)
    const answer = firstResult?.answer?.toUpperCase() === 'YES' ? 'YES'
                 : firstResult?.answer?.toUpperCase() === 'NO' ? 'NO'
                 : null
    rows.push({
      key: `stream-uv-${uv.id}`,
      kind: 'live',
      thumb: uv.thumbnail_data_url,
      prompt,
      createdAt: new Date(uv.created_at),
      durationSeconds: uv.duration_seconds,
      answer: answer as 'YES' | 'NO' | null,
      confidence: firstResult?.confidence != null ? Math.round(firstResult.confidence * 100) : null,
      queryType: firstResult?.query_type ?? undefined,
      userVideoId: uv.id,
    })
  }

  // 3. Persisted upload UserVideos (skip if represented by in-memory pill)
  for (const uv of regularUserVideos) {
    if (inMemoryUserVideoIds.has(uv.id)) continue
    const firstResult = Object.values(uv.results_by_prompt)[0]
    const firstPromptKey = Object.keys(uv.results_by_prompt)[0]
    const prompt = stripCompiledQueryEnvelope(firstPromptKey ?? uv.display_name)
    const answer = firstResult?.answer?.toUpperCase() === 'YES' ? 'YES'
                 : firstResult?.answer?.toUpperCase() === 'NO' ? 'NO'
                 : null
    rows.push({
      key: `upload-uv-${uv.id}`,
      kind: 'upload',
      thumb: uv.thumbnail_data_url,
      prompt,
      createdAt: new Date(uv.created_at),
      durationSeconds: uv.duration_seconds,
      answer: answer as 'YES' | 'NO' | null,
      confidence: firstResult?.confidence != null ? Math.round(firstResult.confidence * 100) : null,
      queryType: firstResult?.query_type ?? undefined,
      userVideoId: uv.id,
    })
  }

  // Sort newest-first
  rows.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
  return rows
}

/** Compute the session count badge: distinct sessions, deduped */
export function computeHistoryCount(
  resultPills: ResultPill[],
  streamUserVideos: UserVideo[],
  regularUserVideos: UserVideo[],
): number {
  const inMemoryUserVideoIds = new Set<string>()
  for (const p of resultPills) {
    if (p.userVideoId) inMemoryUserVideoIds.add(p.userVideoId)
  }
  const inMemoryOnly = resultPills.filter(p => !p.userVideoId)
  const persistedStream = streamUserVideos.filter(uv => !inMemoryUserVideoIds.has(uv.id))
  const persistedUpload = regularUserVideos.filter(uv => !inMemoryUserVideoIds.has(uv.id))
  return inMemoryOnly.length + persistedStream.length + persistedUpload.length + inMemoryUserVideoIds.size
}

// ── Thumbnail fallback ────────────────────────────────────────────────────────

const QUERY_TYPE_COLORS: Record<string, string> = {
  detect: '#1B4FE0',
  count: '#7A4FE0',
  track: '#E0761B',
  classify: '#1BA85E',
}

function ThumbnailFallback({ queryType, size = 56 }: { queryType?: string; size?: number }) {
  const color = (queryType && QUERY_TYPE_COLORS[queryType]) ?? '#7A7669'
  return (
    <div
      aria-hidden="true"
      style={{
        width: size, height: size,
        borderRadius: 8,
        background: `linear-gradient(135deg, ${color}33 0%, ${color}66 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {/* Eye icon (default) or type-specific icon */}
      <svg width={size * 0.4} height={size * 0.4} viewBox="0 0 24 24" fill="none">
        {queryType === 'count' ? (
          // Hash / count icon
          <path d="M4 9h16M4 15h16M9 4v16M15 4v16" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
        ) : queryType === 'track' ? (
          // Target / track icon
          <>
            <circle cx="12" cy="12" r="8" stroke={color} strokeWidth="1.8"/>
            <circle cx="12" cy="12" r="3" fill={color}/>
          </>
        ) : (
          // Eye icon (detect / default)
          <>
            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.8"/>
          </>
        )}
      </svg>
    </div>
  )
}

// ── Swipe-to-delete row ───────────────────────────────────────────────────────

function SwipeRow({
  row,
  onTap,
  onDelete,
}: {
  row: HistoryRow
  onTap: () => void
  onDelete: () => void
}) {
  const [swipeX, setSwipeX] = useState(0)
  const [swiping, setSwiping] = useState(false)
  const startXRef = useRef(0)
  const startYRef = useRef(0)
  const isScrollRef = useRef<boolean | null>(null)
  const DELETE_THRESHOLD = 64

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    startXRef.current = e.clientX
    startYRef.current = e.clientY
    isScrollRef.current = null
    setSwiping(true)
    try { ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId) } catch {}
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!swiping) return
    const dx = e.clientX - startXRef.current
    const dy = Math.abs(e.clientY - startYRef.current)
    // Disambiguate: if first significant movement is vertical, treat as scroll
    if (isScrollRef.current === null) {
      if (Math.abs(dx) > 5 || dy > 5) {
        isScrollRef.current = dy > Math.abs(dx)
      }
    }
    if (isScrollRef.current) { setSwiping(false); return }
    if (dx < 0) {
      e.preventDefault()
      setSwipeX(Math.max(dx, -120))
    } else {
      setSwipeX(0)
    }
  }, [swiping])

  const handlePointerUp = useCallback(() => {
    setSwiping(false)
    if (swipeX < -DELETE_THRESHOLD) {
      // Keep revealed until tapped elsewhere — just snap to -80
      setSwipeX(-80)
    } else {
      setSwipeX(0)
    }
  }, [swipeX])

  const handleTap = useCallback(() => {
    if (Math.abs(swipeX) > 4) {
      setSwipeX(0) // dismiss swipe on tap
      return
    }
    onTap()
  }, [swipeX, onTap])

  const revealed = swipeX < -DELETE_THRESHOLD / 2

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Delete action behind the row */}
      {revealed && (
        <button
          data-testid="history-row-delete"
          onClick={(e) => { e.stopPropagation(); setSwipeX(0); onDelete() }}
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            width: 80,
            background: '#E03B1B',
            border: 'none', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 4, color: '#fff', fontSize: 11, fontWeight: 700,
            minWidth: 44, minHeight: 44,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Delete
        </button>
      )}

      {/* Row content */}
      <div
        data-testid="history-row"
        role="button"
        tabIndex={0}
        aria-label={`Session: ${row.prompt}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 16px',
          minHeight: 72,
          background: L.bgSurface,
          borderBottom: `1px solid ${L.border}`,
          cursor: 'pointer',
          transform: `translateX(${swipeX}px)`,
          transition: swiping ? 'none' : 'transform 200ms ease',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onClick={handleTap}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleTap() } }}
      >
        {/* Thumbnail */}
        {row.thumb ? (
          <img
            src={row.thumb}
            alt=""
            loading="lazy"
            style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', flexShrink: 0, background: L.bgSecondary }}
          />
        ) : (
          <ThumbnailFallback queryType={row.queryType} size={56} />
        )}

        {/* Text content */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Prompt */}
          <span style={{
            fontSize: 13, fontWeight: 600, color: L.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row.prompt || 'No prompt'}
          </span>

          {/* Meta row: source chip · time · duration */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 10, fontWeight: 700,
              color: row.kind === 'live' ? '#E03B1B' : L.signal,
              background: row.kind === 'live' ? '#E03B1B1A' : `${L.signal}1A`,
              border: `1px solid ${row.kind === 'live' ? '#E03B1B40' : `${L.signal}40`}`,
              borderRadius: 4, padding: '1px 5px',
            }}>
              {row.kind === 'live' ? 'Live' : 'Upload'}
            </span>
            <span style={{ fontSize: 11, color: L.textMuted }}>
              {formatTime(row.createdAt)}
            </span>
            {row.durationSeconds != null && (
              <span style={{ fontSize: 11, color: L.textMuted }}>
                {fmtTime(row.durationSeconds)}
              </span>
            )}
          </div>
        </div>

        {/* Verdict chip */}
        {row.answer != null && (
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <span style={{
              fontSize: 11, fontWeight: 700,
              // Verdict palette shared with V2ResultPill mobile card (eval finding:
              // history rows and result cards render adjacent — keep colors identical).
              color: row.answer === 'YES' ? '#16A34A' : '#DC2626',
              background: row.answer === 'YES' ? 'rgba(22,163,74,0.12)' : 'rgba(220,38,38,0.10)',
              border: `1px solid ${row.answer === 'YES' ? 'rgba(22,163,74,0.25)' : 'rgba(220,38,38,0.25)'}`,
              borderRadius: 6, padding: '2px 6px',
            }}>
              {row.answer}
            </span>
            {row.confidence != null && (
              <span style={{ fontSize: 10, color: L.textMuted }}>{row.confidence}%</span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Virtualized list ──────────────────────────────────────────────────────────

const ROW_HEIGHT_APPROX = 73 // px per row (approximate)
const VIRTUALIZE_THRESHOLD = 50

interface VirtualListProps {
  rows: HistoryRow[]
  onTap: (row: HistoryRow) => void
  onDelete: (row: HistoryRow) => void
}

function VirtualList({ rows, onTap, onDelete }: VirtualListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [scrollTop, setScrollTop] = useState(0)

  const useVirtualize = rows.length > VIRTUALIZE_THRESHOLD

  const visibleStart = useVirtualize
    ? Math.max(0, Math.floor(scrollTop / ROW_HEIGHT_APPROX) - 3)
    : 0
  const visibleEnd = useVirtualize
    ? Math.min(rows.length, Math.ceil((scrollTop + 600) / ROW_HEIGHT_APPROX) + 3)
    : rows.length

  const paddingTop = useVirtualize ? visibleStart * ROW_HEIGHT_APPROX : 0
  const paddingBottom = useVirtualize ? (rows.length - visibleEnd) * ROW_HEIGHT_APPROX : 0

  const handleScroll = useCallback(() => {
    if (containerRef.current) setScrollTop(containerRef.current.scrollTop)
  }, [])

  // Group visible rows by date label
  const grouped: Array<{ label: string; rows: HistoryRow[] }> = []
  for (let i = visibleStart; i < visibleEnd; i++) {
    const row = rows[i]
    const label = groupLabel(row.createdAt)
    const last = grouped[grouped.length - 1]
    if (!last || last.label !== label) {
      grouped.push({ label, rows: [row] })
    } else {
      last.rows.push(row)
    }
  }

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{ overflowY: 'auto', overscrollBehavior: 'contain', flex: 1 }}
    >
      <div style={{ paddingTop, paddingBottom }}>
        {grouped.map(group => (
          <div key={group.label}>
            {/* Sticky date header */}
            <div
              style={{
                position: 'sticky', top: 0, zIndex: 2,
                padding: '6px 16px 4px',
                background: L.bgPrimary,
                fontSize: 11, fontWeight: 700,
                color: L.textMuted,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                borderBottom: `1px solid ${L.border}`,
              }}
            >
              {group.label}
            </div>
            {group.rows.map(row => (
              <SwipeRow
                key={row.key}
                row={row}
                onTap={() => onTap(row)}
                onDelete={() => onDelete(row)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onStart }: { onStart: () => void }) {
  return (
    <div
      data-testid="history-empty"
      style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 16, padding: '32px 24px', textAlign: 'center',
      }}
    >
      {/* Inline SVG illustration */}
      <svg width="72" height="72" viewBox="0 0 72 72" fill="none" aria-hidden="true">
        <circle cx="36" cy="36" r="32" fill={`${L.signal}14`}/>
        <circle cx="36" cy="36" r="22" stroke={L.border} strokeWidth="1.5"/>
        <path d="M36 22v14l8 8" stroke={L.signal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx="36" cy="36" r="2" fill={L.signal}/>
      </svg>
      <div>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: L.textPrimary, lineHeight: 1.4 }}>
          Your sessions will appear here
        </p>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: L.textMuted, lineHeight: 1.5 }}>
          Run a live stream or analyse a video to get started.
        </p>
      </div>
      <button
        onClick={onStart}
        style={{
          background: L.signal, color: '#fff',
          border: 'none', borderRadius: 10,
          padding: '12px 24px', fontSize: 14, fontWeight: 600,
          cursor: 'pointer', minHeight: 44, minWidth: 44,
        }}
      >
        Start a session
      </button>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function MobileSessionHistory({
  open,
  onClose,
  isStreaming,
  streamPillIdRef,
  resultPills,
  streamUserVideos,
  regularUserVideos,
  handleDeleteResult,
  deleteVideo,
  handleSelectUserVideo,
  attachRenderedVideoForced,
  setVideoMenuOpen,
}: MobileSessionHistoryProps) {
  const [filter, setFilter] = useState<HistoryFilter>('all')
  const analyticsOpenedRef = useRef(false)

  // Build merged rows
  const allRows = buildRows(resultPills, streamUserVideos, regularUserVideos)

  // Fire analytics when overlay opens (once per open)
  useEffect(() => {
    if (open && !analyticsOpenedRef.current) {
      analyticsOpenedRef.current = true
      trackMobileHistoryOpened({ session_count: allRows.length })
    }
    if (!open) {
      analyticsOpenedRef.current = false
    }
  }, [open, allRows.length])

  // Reset filter when re-opened
  useEffect(() => {
    if (!open) setFilter('all')
  }, [open])

  // Filter rows
  const filteredRows = allRows.filter(row => {
    if (filter === 'live') return row.kind === 'live'
    if (filter === 'uploads') return row.kind === 'upload'
    return true
  })

  // Is there a currently active stream pill?
  const liveStreamPillId = isStreaming && streamPillIdRef.current ? streamPillIdRef.current : null
  const liveStreamPill = liveStreamPillId
    ? resultPills.find(p => p.id === liveStreamPillId)
    : null

  const handleFilterChange = useCallback((f: HistoryFilter) => {
    setFilter(f)
    trackMobileHistoryFilterUsed({ filter: f })
  }, [])

  const handleRowTap = useCallback((row: HistoryRow) => {
    onClose()
    if (row.pill) {
      // In-memory pill — already in results frame, close overlay.
      // For upload pills without a video URL, trigger video loading.
      trackMobileSessionReopened({ source: row.kind, persisted: false })
      if (row.kind === 'upload' && !row.pill.videoUrl) {
        attachRenderedVideoForced(row.pill.id)
      }
    } else if (row.userVideoId) {
      // Persisted session — reopen ONLY the tapped session via getVideoSession
      // (handleSelectUserVideo works for both stream + upload UserVideos; its
      // sessionEntriesToPills handles query_type==='stream' rows, and stream
      // pills with recordingUrl expose playback via the existing onPlayVideo →
      // attachStreamRecordingUrlForced path in MobileLayout). Do NOT use
      // handleSelectLiveStreams here — it bulk-loads the ENTIRE live archive,
      // not the single session the user tapped (eval finding, PRI-494 P3).
      trackMobileSessionReopened({ source: row.kind, persisted: true })
      void handleSelectUserVideo(row.userVideoId)
    }
  }, [onClose, handleSelectUserVideo, attachRenderedVideoForced])

  const handleRowDelete = useCallback((row: HistoryRow) => {
    if (row.pill) {
      handleDeleteResult(row.pill)
    } else if (row.userVideoId) {
      deleteVideo(row.userVideoId)
    }
  }, [handleDeleteResult, deleteVideo])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Session history"
      data-testid="mobile-session-history"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        background: L.bgPrimary,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          height: MOBILE_ZONES.header + 8,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          background: L.bgPrimary,
          borderBottom: `1px solid ${L.border}`,
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px 0 16px',
          flexShrink: 0,
          gap: 8,
        }}
      >
        <span style={{ flex: 1, fontSize: 16, fontWeight: 700, color: L.textPrimary }}>
          History
        </span>
        <button
          aria-label="Close session history"
          onClick={onClose}
          style={{
            width: 44, height: 44,
            background: 'none', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: L.textMuted, borderRadius: 8, padding: 0,
            fontSize: 20, lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Pinned LIVE row */}
      {liveStreamPill && (
        <button
          data-testid="history-live-row"
          onClick={onClose}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 16px',
            minHeight: 52,
            background: '#E03B1B14',
            border: 'none', cursor: 'pointer',
            borderBottom: `2px solid #E03B1B40`,
            width: '100%', textAlign: 'left',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {/* Pulsing LIVE dot */}
          <span style={{ position: 'relative', width: 10, height: 10, flexShrink: 0 }}>
            <span style={{
              position: 'absolute', inset: 0,
              borderRadius: '50%',
              background: '#E03B1B',
              animation: 'pv2-highlight-pulse 1.2s ease-in-out infinite',
            }} />
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#E03B1B' }}>LIVE</span>
          <span style={{ fontSize: 13, color: L.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {stripCompiledQueryEnvelope(liveStreamPill.prompt) || 'Active stream'}
          </span>
          <span style={{ fontSize: 11, color: L.textMuted }}>Return ›</span>
        </button>
      )}

      {/* Filter chips */}
      <div
        role="tablist"
        aria-label="Session filter"
        style={{
          display: 'flex', gap: 8,
          padding: '10px 16px 8px',
          flexShrink: 0,
          borderBottom: `1px solid ${L.border}`,
        }}
      >
        {(['all', 'live', 'uploads'] as HistoryFilter[]).map(f => (
          <button
            key={f}
            role="tab"
            aria-pressed={filter === f}
            aria-selected={filter === f}
            data-testid={`history-filter-${f}`}
            onClick={() => handleFilterChange(f)}
            style={{
              padding: '6px 16px',
              border: `1px solid ${filter === f ? L.signal : L.border}`,
              borderRadius: 22,
              background: filter === f ? `${L.signal}14` : 'none',
              color: filter === f ? L.signal : L.textMuted,
              fontSize: 13, fontWeight: filter === f ? 700 : 400,
              // ≥44pt tap target (UX addendum item 8; eval finding)
              cursor: 'pointer', minHeight: 44, minWidth: 44,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Body */}
      {filteredRows.length === 0 ? (
        <EmptyState onStart={() => { onClose(); setVideoMenuOpen(true) }} />
      ) : (
        <VirtualList
          rows={filteredRows}
          onTap={handleRowTap}
          onDelete={handleRowDelete}
        />
      )}
    </div>
  )
}
